import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Heart,
  MessageCircle,
  RefreshCw,
  Search,
  ThumbsDown,
  Users,
  X,
} from "lucide-react";
import { GetTeamMemberAssignedUsers, getTeamMemberActivity } from "./apis/get";
import { PostTeamActivityAcknowledge } from "./apis/post";

const EVENT_OPTIONS = [
  { value: "all", label: "All activity" },
  { value: "published", label: "Published" },
  { value: "unpublished_match", label: "Unpublish" },
  { value: "like_sent", label: "Likes Sent" },
  { value: "dislike_sent", label: "Dislikes Sent" },
  { value: "like_received", label: "Likes Received" },
  { value: "dislike_received", label: "Dislikes Received" },
  { value: "message_received", label: "Messages Received" },
  { value: "message_sent", label: "Messages Sent" },
  { value: "template_sent", label: "Templates Sent" },
  { value: "template_failed", label: "Templates Failed" },
];

const PAGE_SIZE = 20;

const toCount = (value: unknown) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return "--";
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasTimezone ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return value;
  return `${new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date)} IST`;
};

const getInitials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "TM";

const normalizeText = (value: unknown, fallback = "--") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const formatTemplateDisplayName = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

type TemplateBodySpec = {
  body: string;
  parameterKeys: string[];
};

const TEMPLATE_BODY_SPECS: Record<string, TemplateBodySpec> = {
  networktroubleshooting20: {
    body: "Hi, we have {line_1}\n\n1 {line_2},\n2 {line_3},\n3 {line_4}.\nNeed more help? Contact: {line_5} or view details.",
    parameterKeys: ["line_1", "line_2", "line_3", "line_4", "line_5"],
  },
  astrologysurveytemp: {
    body: "Namaste!\n\n{line_1}\n\nLekin humne dekha hai ki kuch members ke liye astrology utni important nahi hoti. Isliye hum aapse jaana chahte hain:\n\n*Aapke liye astrology kitni important hai?*\n{line_2}\n{line_3}\n{line_4}\n\nAapka response humein aapke liye aur behtar matches suggest karne mein madad Karega.",
    parameterKeys: ["line_1", "line_2", "line_3", "line_4"],
  },
  astrologyimportancesurveytemp: {
    body: "Namaste!\n\n{line_1}\n\nLekin humne dekha hai ki kuch members ke liye astrology utni important nahi hoti. Isliye hum aapse jaana chahte hain:\n\n*Aapke liye astrology kitni important hai?*\n{line_2}\n{line_3}\n{line_4}\n\nAapka response humein aapke liye aur behtar matches suggest karne mein madad Karega.",
    parameterKeys: ["line_1", "line_2", "line_3", "line_4"],
  },
  smmfeedbacktempnew: {
    body: "Hi {user_name}\n\nAapka experience Smart Matrimony par kaisa chal raha hai?\n{line_1}\n\nAgar aap satisfied hain, toh humein zaroor batayein - {line_2}\n{line_3}\n\nAapka trust hi humari strength hai.\nThanks for being part of Smart Matrimony.",
    parameterKeys: ["user_name", "line_1", "line_2", "line_3"],
  },
  manglikstatusconfirmationtemplate: {
    body: "Namaste!\n\nSmart Matrimony mein hum aapko sabse sahi aur compatible matches suggest karne ke liye continuously apni matching quality improve kar rahe hain.\n\nAapki profile mein aapka Manglik Status *{profile_manglik_status}* diya gaya hai.\n\nLekin hamare AI-based analysis ke according, aapki profile details se aapka Manglik Status *{ai_manglik_status}* detect ho raha hai.\n\nKripya confirm karein ki inmein se kaunsa sahi hai:\n\n{profile_manglik_status_option} (Profile me diya gaya)\n{ai_manglik_status_option} (AI detection ke according)\n\nAapki confirmation se humein aapke liye aur bhi accurate aur better compatible matches suggest karne mein madad milegi.\n\nDhanyavaad!\nTeam Smart Matrimony",
    parameterKeys: ["profile_manglik_status", "ai_manglik_status", "profile_manglik_status_option", "ai_manglik_status_option"],
  },
  smmincometemp: {
    body: "Hi {user_name}\n\nKayi baar achhe rishte sirf isliye aage nahi badh paate kyunki profile par mentioned financial income accurate nahi hoti.\n\nBetter & compatible matches suggest karne ke liye hum aapki annual income reconfirm karna chahte hain.\n\nAbhi aapki profile par annual income: *{current_income}* mentioned hai.\nKya yeh correct hai?\n\n{line_3}\n\n{line_4}\n\nThank you,\nTeam SmartMatrimony",
    parameterKeys: ["user_name", "current_income", "line_3", "line_4"],
  },
  smmseriousnesschecknew: {
    body: "Hi {user_name}\n\n{line_1}\n\nHume kabhi-kabhi non-serious responses milte hain, isliye hum profiles ko regularly verify karte rehte hain.\n\n{line_2}\n\nAgar aap abhi shaadi ke liye active nahi hain, toh please hume bata dein. Hum aapki profile temporarily disable kar denge taaki sirf serious members active rahein.\n\n{line_3}\n\n{line_4}\n\nThank you for understanding and supporting a serious matchmaking platform.\nTeam SmartMatrimony",
    parameterKeys: ["user_name", "line_1", "line_2", "line_3", "line_4"],
  },
  smmmatchestemp: {
    body: "Hi {user_name},\n\nThese are the details of your latest new match:\nName: {candidate_name}\nAge: {candidate_age}\nCity: {candidate_location}\nPersonality Score: {personality_score}\nGunn Score: {gunn_score}\n\n{extra_message}\n\nThank you,\nSmart Matrimony Support Team",
    parameterKeys: ["user_name", "candidate_name", "candidate_age", "candidate_location", "personality_score", "gunn_score", "extra_message"],
  },
  premiumupgradeoffer: {
    body: "Namaste {user_name}\n\n{line_1}\n\n{line_2}\n\n{line_3}\n{line_4}\nSamellan-style family introductions & meetups\n{line_5}\nMatch coordination aur mediation support\nSerious families tak better reach\n\nHum sirf profiles nahi dikhate - hamari team rishton ko aage badhane mein actively madad karti hai.\n\nPremium Membership activate karne ke liye is message par reply karein.\n\nTeam Smart Matrimony",
    parameterKeys: ["user_name", "line_1", "line_2", "line_3", "line_4", "line_5"],
  },
  renewpremiummembership: {
    body: "Namaste {user_name}\n\n{line_1}\n\n{line_2}\n\n{line_3}\nSamellan-style family introductions & meetups\n{line_4}\nMatch coordination aur mediation support\n{line_5}\nSerious families tak better reach\n\n{line_6}\nHum sirf profiles nahi dikhate - hamari team rishton ko aage badhane mein actively madad karti hai.\n\nPremium Membership activate karne ke liye is message par reply karein.\n\nTeam Smart Matrimony",
    parameterKeys: ["user_name", "line_1", "line_2", "line_3", "line_4", "line_5", "line_6"],
  },
  sendcommunitythankyoutemplate: {
    body: "Thank You {community_name}!\n{line_1}\n{line_2}\n{line_3}\nDil se thank you har ek member ko is journey ka hissa banne ke liye.\nAise hi apna pyaar aur support banaye rakhiye - abhi toh bahut si kahaniyan likhni baaki hain!",
    parameterKeys: ["community_name", "line_1", "line_2", "line_3"],
  },
  smmelanvideotemplate: {
    body: "{line_1}\n{line_2}\n{line_3}\n{line_4}\n{support_contact}",
    parameterKeys: ["line_1", "line_2", "line_3", "line_4", "support_contact"],
  },
};

const cleanTemplateBody = (value: string) =>
  value
    .split(/\r?\n/)
    .filter((line) => {
      const trimmed = line.trim().toLowerCase();
      return !trimmed.startsWith("template_values:") && !trimmed.startsWith("buttons:");
    })
    .join("\n")
    .trim();

const getTemplateParameterEntries = (parameters?: Record<string, unknown> | null) => {
  if (!parameters) return [];
  const values = isRecord(parameters.template_values) ? parameters.template_values : parameters;
  return Object.entries(values).filter(([key, value]) => {
    if (["buttons", "template_values", "language_code"].includes(key)) return false;
    return value !== undefined && value !== null && value !== "";
  });
};

const getTemplateValues = (parameters?: Record<string, unknown> | null) => {
  if (!parameters) return {};
  return isRecord(parameters.template_values) ? parameters.template_values : parameters;
};

const normalizeTemplateKey = (value: unknown) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const formatParameterValue = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => formatParameterValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (isRecord(value)) {
    return Object.entries(value)
      .map(([key, item]) => `${key}: ${formatParameterValue(item)}`)
      .join(", ");
  }
  return String(value);
};

const resolveTemplateBody = (
  body: string,
  parameters?: Record<string, unknown> | null,
  parameterOrder: string[] = [],
) => {
  let resolved = cleanTemplateBody(body);
  const values = getTemplateValues(parameters);
  const entries = Object.entries(getTemplateValues(parameters)).filter(([key, value]) => {
    if (["buttons", "template_values", "language_code"].includes(key)) return false;
    return value !== undefined && value !== null && value !== "";
  });

  if (!resolved || entries.length === 0) return resolved;

  const valueByKey = entries.reduce<Record<string, string>>((acc, [key, value]) => {
    const formattedValue = formatParameterValue(value);
    acc[String(key)] = formattedValue;
    acc[normalizeTemplateKey(key)] = formattedValue;
    return acc;
  }, {});
  parameterOrder.forEach((key, index) => {
    const value = values[key] ?? values[String(index + 1)];
    if (value === undefined || value === null || value === "") return;
    const formattedValue = formatParameterValue(value);
    valueByKey[key] = formattedValue;
    valueByKey[normalizeTemplateKey(key)] = formattedValue;
  });

  resolved = resolved.replace(/{{\s*(\d+)\s*}}/g, (match, index) => {
    const numericValue = valueByKey[String(index)];
    if (numericValue !== undefined) return numericValue;
    const positionalValue = entries[Number(index) - 1]?.[1];
    return positionalValue !== undefined ? formatParameterValue(positionalValue) : match;
  });

  resolved = resolved.replace(/{\s*([A-Za-z0-9_ -]+)\s*}/g, (match, key) => {
    const value = valueByKey[String(key)] ?? valueByKey[normalizeTemplateKey(key)];
    return value !== undefined ? value : match;
  });

  resolved = resolved.replace(/<\s*([^<>]+?)\s*>/g, (match, key) => {
    const value = valueByKey[String(key)] ?? valueByKey[normalizeTemplateKey(key)];
    return value !== undefined ? value : match;
  });

  return resolved;
};

const buildLegacyPremiumMatchBody = (parameters?: Record<string, unknown> | null) => {
  const values = getTemplateValues(parameters);
  const candidateName = formatParameterValue(values.candidate_name);
  const selectedIntro = formatParameterValue(values.selected_intro);
  const personalityScore = formatParameterValue(values.personality_score);
  const gunnScore = formatParameterValue(values.gunn_score);
  const supportContact = formatParameterValue(values.support_contact);

  if (!candidateName && !selectedIntro && !personalityScore && !gunnScore && !supportContact) {
    return "";
  }

  return [
    `Hi, we have have Matched ${candidateName || "this profile"} with you. ${selectedIntro}`.trim(),
    "",
    `1 Name: ${candidateName || "--"},`,
    `2 Personality Score: ${personalityScore || "--"},`,
    `3 Gunn Score: ${gunnScore || "--"}.`,
    `Need more help? Contact: ${supportContact || "--"} or view details.`,
  ].join("\n");
};

const parseTemplateDetail = (
  detail?: string,
  templateName?: string,
  parameters?: Record<string, unknown> | null,
) => {
  const text = normalizeText(detail, "Template Message");
  const cleanText = cleanTemplateBody(text) || text;
  const normalizedTemplateName = normalizeTemplateKey(templateName);
  const templateSpec = TEMPLATE_BODY_SPECS[normalizedTemplateName];
  const isNumberedValueDump = cleanText
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .every((line) => /^\d+\s*:/.test(line.trim()));
  const templateSpecBody = templateSpec && (isNumberedValueDump || /{{\s*\d+\s*}}|{\s*[A-Za-z0-9_ -]+\s*}|<\s*line\s+\d+\s*>/i.test(cleanText))
    ? resolveTemplateBody(templateSpec.body, parameters, templateSpec.parameterKeys)
    : "";
  const hasLegacyLinePlaceholders = /<\s*line\s+\d+\s*>/i.test(cleanText);
  const legacyPremiumBody = normalizedTemplateName === "networktroubleshooting20" && hasLegacyLinePlaceholders
    ? buildLegacyPremiumMatchBody(parameters)
    : "";

  if (templateName) {
    return {
      title: formatTemplateDisplayName(templateName),
      body: legacyPremiumBody || templateSpecBody || resolveTemplateBody(cleanText, parameters),
    };
  }

  const separatorIndex = cleanText.indexOf(": ");
  if (separatorIndex <= 0) {
    return {
      title: formatTemplateDisplayName(cleanText),
      body: legacyPremiumBody || templateSpecBody || resolveTemplateBody(cleanText, parameters),
    };
  }

  return {
    title: formatTemplateDisplayName(cleanText.slice(0, separatorIndex).trim() || "Template Message"),
    body: legacyPremiumBody || templateSpecBody || resolveTemplateBody(cleanText.slice(separatorIndex + 2).trim() || cleanText, parameters),
  };
};

const eventStyles: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  published: {
    label: "Published",
    icon: CheckCircle2,
    className: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  },
  unpublished_match: {
    label: "Unpublish",
    icon: Activity,
    className: "border-cyan-500/25 bg-cyan-500/10 text-cyan-300",
  },
  like_sent: {
    label: "Like Sent",
    icon: Heart,
    className: "border-sky-500/25 bg-sky-500/10 text-sky-300",
  },
  dislike_sent: {
    label: "Dislike Sent",
    icon: ThumbsDown,
    className: "border-rose-500/25 bg-rose-500/10 text-rose-300",
  },
  like_received: {
    label: "Like Received",
    icon: Heart,
    className: "border-violet-500/25 bg-violet-500/10 text-violet-300",
  },
  dislike_received: {
    label: "Dislike Received",
    icon: ThumbsDown,
    className: "border-amber-500/25 bg-amber-500/10 text-amber-300",
  },
  message_received: {
    label: "Message Received",
    icon: MessageCircle,
    className: "border-fuchsia-500/25 bg-fuchsia-500/10 text-fuchsia-300",
  },
  message_sent: {
    label: "Message Sent",
    icon: MessageCircle,
    className: "border-indigo-500/25 bg-indigo-500/10 text-indigo-300",
  },
  template_sent: {
    label: "Template Sent",
    icon: MessageCircle,
    className: "border-teal-500/25 bg-teal-500/10 text-teal-300",
  },
  template_failed: {
    label: "Template Failed",
    icon: AlertCircle,
    className: "border-red-500/30 bg-red-500/10 text-red-300",
  },
};

type TeamMember = {
  id?: string | number;
  username: string;
  role?: string;
  assigned_count?: number;
};

type ActivityRow = {
  activity_key?: string;
  event_type: string;
  event_time?: string;
  user_id?: string;
  user_name?: string;
  user_is_premium?: boolean;
  candidate_id?: string;
  candidate_name?: string;
  candidate_is_premium?: boolean;
  detail?: string;
  template_name?: string;
  template_language?: string;
  template_parameters?: Record<string, unknown> | null;
  source?: string;
  reason?: string;
  acknowledged?: boolean;
  acknowledged_at?: string;
  acknowledged_by_admin_id?: string;
  acknowledged_by_username?: string;
};

const extractSupportAdmins = (response: any): TeamMember[] => {
  let adminData: TeamMember[] = [];
  if (Array.isArray(response)) adminData = response;
  else if (response && Array.isArray(response.data)) adminData = response.data;
  else if (response && response.data && Array.isArray(response.data.data)) adminData = response.data.data;
  else if (response && response.status === "success" && Array.isArray(response.data)) adminData = response.data;

  const excluded = ["soniyarathi"];
  adminData = adminData.filter(
    (admin) => admin.username && !excluded.includes(admin.username.toLowerCase()),
  );

  const hasSoniya = adminData.some((admin) => {
    const name = (admin.username || "").toLowerCase();
    return name === "soniya" || name === "soniya rathi";
  });

  if (!hasSoniya) {
    adminData.push({
      id: "soniya_rathi_manual",
      username: "Soniya Rathi",
      role: "Support Agent",
    });
  }

  return adminData;
};

const PremiumBadge = () => (
  <span className="inline-flex items-center rounded border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-200">
    Premium
  </span>
);

const AssignedUserBadge = () => (
  <span className="inline-flex items-center rounded border border-blue-400/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-200">
    Assigned User
  </span>
);

type TemplateModalState = {
  title: string;
  body: string;
  language?: string;
  parameters?: Record<string, unknown> | null;
  sentAt?: string;
  userName?: string;
};

type AssignedUser = {
  id: string;
  name?: string;
  progress?: number;
  status?: string;
  is_active?: boolean;
  is_hold?: boolean;
  gender?: string;
  location?: string;
  is_verified?: boolean;
  assigned_at?: string | null;
};

export default function TeamMemberActivity() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [summary, setSummary] = useState<Record<string, unknown>>({});
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [eventType, setEventType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [acknowledgingKey, setAcknowledgingKey] = useState("");
  const [templateModal, setTemplateModal] = useState<TemplateModalState | null>(null);
  const [showAssignedUsers, setShowAssignedUsers] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [assignedUsersPage, setAssignedUsersPage] = useState(1);
  const [assignedUsersTotal, setAssignedUsersTotal] = useState(0);
  const [assignedUsersLoading, setAssignedUsersLoading] = useState(false);
  const assignedUsersCacheRef = React.useRef(
    new Map<string, { users: AssignedUser[]; total: number }>(),
  );
  const templateParameterEntries = useMemo(
    () => getTemplateParameterEntries(templateModal?.parameters),
    [templateModal?.parameters],
  );

  const mergeAssignedCounts = useCallback((members: TeamMember[]) => {
    setTeamMembers((previous) => {
      if (!previous.length) return members;
      const countByUsername = new Map(
        members.map((item) => [
          String(item.username || "").toLowerCase(),
          item.assigned_count,
        ]),
      );
      return previous.map((item) => ({
        ...item,
        assigned_count: countByUsername.get(String(item.username || "").toLowerCase()) ?? item.assigned_count,
      }));
    });
  }, []);

  const fetchActivity = useCallback(
    async (member: string, nextOffset: number, append = false) => {
      setLoading(true);
      setError("");
      if (!append) {
        setActivity([]);
        setSummary({});
        setHasMore(false);
      }
      try {
        const requestLimit = PAGE_SIZE;
        const response = await getTeamMemberActivity({
          assigned_to: member,
          user_search: userSearch,
          start_date: startDate,
          end_date: endDate,
          event_type: eventType,
          limit: requestLimit,
          offset: nextOffset,
          include_metadata: !append,
        });
        const data = response?.data || {};
        const nextRows: ActivityRow[] = Array.isArray(data.activity)
          ? data.activity.slice(0, requestLimit)
          : [];
        if (!append) {
          const responseMembers = extractSupportAdmins({
            data: data.team_members || [],
          });
          if (data.access?.can_view_all_team_members === false) {
            setTeamMembers(responseMembers);
          } else {
            mergeAssignedCounts(responseMembers);
          }
          setSelectedMember(data.selected_assigned_to || member || "");
          setSummary(data.summary || {});
          setActivity(nextRows);
        } else {
          setActivity((previous) => [...previous, ...nextRows]);
        }
        const serverHasMore = data.pagination?.has_more;
        setHasMore(
          typeof serverHasMore === "boolean"
            ? serverHasMore
            : nextRows.length === requestLimit,
        );
      } catch (err: any) {
        console.error("Failed to fetch team member activity", err);
        setError(err?.message || "Failed to fetch team activity");
      } finally {
        setLoading(false);
      }
    },
    [endDate, eventType, mergeAssignedCounts, startDate, userSearch],
  );

  const fetchSupportMembers = useCallback(async () => {
    setLoadingMembers(true);
    setError("");
    try {
      await fetchActivity("", 0);
    } finally {
      setLoadingMembers(false);
    }
  }, [fetchActivity]);

  useEffect(() => {
    fetchSupportMembers();
    // Support members should load once from the same source as SalesTeamDashboard.
    // Activity refreshes separately when filters or selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedMember) {
      fetchActivity(selectedMember, 0);
    }
    // Selection is fetched by handleSelectMember; this effect reacts only to filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, startDate, endDate]);

  const filteredMembers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return teamMembers;
    return teamMembers.filter((member) =>
      `${member.username || ""} ${member.role || ""}`.toLowerCase().includes(needle),
    );
  }, [search, teamMembers]);

  const totals = [
    { label: "Assigned Users", value: summary.assigned_users, tone: "text-slate-100" },
    { label: "Unpublish", value: summary.unpublished_matches, tone: "text-cyan-300" },
    { label: "Published", value: summary.published, tone: "text-emerald-300" },
    { label: "Likes Sent", value: summary.likes_sent, tone: "text-sky-300" },
    { label: "Dislikes Sent", value: summary.dislikes_sent, tone: "text-rose-300" },
    { label: "Likes Received", value: summary.likes_received, tone: "text-violet-300" },
    { label: "Dislikes Received", value: summary.dislikes_received, tone: "text-amber-300" },
    { label: "Msg Received", value: summary.messages_received, tone: "text-fuchsia-300" },
    { label: "Msg Sent", value: summary.messages_sent, tone: "text-indigo-300" },
    { label: "Templates", value: summary.templates_sent, tone: "text-teal-300" },
    { label: "Template Failed", value: summary.templates_failed, tone: "text-red-300" },
  ];

  const handleSelectMember = (username: string) => {
    setSelectedMember(username);
    fetchActivity(username, 0);
  };

  const fetchAssignedUsers = useCallback(async (page = 1) => {
    if (!selectedMember) return;
    const cacheKey = `${selectedMember.toLowerCase()}:${page}`;
    const cachedPage = assignedUsersCacheRef.current.get(cacheKey);
    if (cachedPage) {
      setAssignedUsers(cachedPage.users);
      setAssignedUsersTotal(cachedPage.total);
      setAssignedUsersPage(page);
      setShowAssignedUsers(true);
      return;
    }
    setAssignedUsersLoading(true);
    setError("");
    try {
      const limit = 20;
      const response = await GetTeamMemberAssignedUsers(
        selectedMember,
        limit,
        (page - 1) * limit,
      );
      const data = response?.data || {};
      const rows = data.users || [];
      const normalizedRows = Array.isArray(rows) ? rows : [];
      const total = toCount(data.pagination?.total);
      assignedUsersCacheRef.current.set(cacheKey, {
        users: normalizedRows,
        total,
      });
      setAssignedUsers(normalizedRows);
      setAssignedUsersTotal(total);
      setAssignedUsersPage(page);
      setShowAssignedUsers(true);
    } catch (err: any) {
      setError(err?.message || "Failed to load assigned users");
    } finally {
      setAssignedUsersLoading(false);
    }
  }, [selectedMember]);

  const openAssignedUsers = useCallback(() => {
    assignedUsersCacheRef.current.clear();
    fetchAssignedUsers(1);
  }, [fetchAssignedUsers]);

  const loadMoreInFlightRef = React.useRef(false);
  const handleLoadMore = useCallback(async () => {
    if (
      loading ||
      loadMoreInFlightRef.current ||
      !hasMore
    ) {
      return;
    }
    loadMoreInFlightRef.current = true;
    try {
      await fetchActivity(selectedMember, activity.length, true);
    } finally {
      loadMoreInFlightRef.current = false;
    }
  }, [activity.length, selectedMember, fetchActivity, hasMore, loading]);

  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMore &&
        activity.length > 0
      ) {
        handleLoadMore();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, handleLoadMore, hasMore, activity.length]);

  const handleAcknowledge = async (item: ActivityRow) => {
    if (!item.activity_key || item.acknowledged) return;
    setAcknowledgingKey(item.activity_key);
    setError("");
    try {
      const response = await PostTeamActivityAcknowledge({
        activity_key: item.activity_key,
        event_type: item.event_type,
        assigned_to: selectedMember,
        user_id: item.user_id,
        candidate_id: item.candidate_id,
        event_time: item.event_time,
        source: item.source,
      });
      if (!response || response.status !== true) {
        throw new Error(response?.detail || response?.message || "Failed to acknowledge activity");
      }
      const acknowledgement = response?.data || {};
      setActivity((previous) =>
        previous.map((row) =>
          row.activity_key === item.activity_key
            ? {
              ...row,
              acknowledged: true,
              acknowledged_at: acknowledgement.acknowledged_at || new Date().toISOString(),
              acknowledged_by_admin_id: acknowledgement.acknowledged_by_admin_id,
              acknowledged_by_username: acknowledgement.acknowledged_by_username,
            }
            : row,
        ),
      );
    } catch (err: any) {
      console.error("Failed to acknowledge team activity", err);
      setError(err?.message || "Failed to acknowledge activity");
    } finally {
      setAcknowledgingKey("");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mb-5 flex flex-col gap-3 border-b border-slate-800 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            <Activity size={14} />
            Team Activity
          </div>
          <h1 className="mt-2 text-2xl font-semibold text-white">Assigned User Timeline</h1>
        </div>
        <button
          type="button"
          onClick={() => fetchActivity(selectedMember, 0)}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-slate-500 hover:bg-slate-800"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="h-fit rounded-md border border-slate-800 bg-slate-900/50">
          <div className="border-b border-slate-800 p-4">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              <Users size={14} />
              Active Support Agents
            </div>
            <label className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-300">
              <Search size={15} className="text-slate-500" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search team member"
                className="w-full bg-transparent outline-none placeholder:text-slate-600"
              />
            </label>
          </div>
          <div className="max-h-[calc(100vh-250px)] space-y-2 overflow-y-auto p-3">
            {loadingMembers ? (
              <div className="py-16 flex justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500" />
              </div>
            ) : filteredMembers.length > 0 ? (
              filteredMembers.map((member) => {
                const active = member.username === selectedMember;
                return (
                  <button
                    key={member.id || member.username}
                    type="button"
                    onClick={() => handleSelectMember(member.username)}
                    className={`flex w-full items-center gap-3 rounded-md border px-3 py-3 text-left transition ${active
                      ? "border-blue-500 bg-blue-600 text-white"
                      : "border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700 hover:bg-slate-900"
                      }`}
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-xs font-bold ${active ? "bg-white text-blue-700" : "bg-slate-800 text-slate-300"
                      }`}>
                      {getInitials(member.username)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{member.username}</span>
                      <span className={`mt-0.5 block truncate text-[10px] font-semibold uppercase tracking-wider ${active ? "text-blue-100" : "text-slate-500"
                        }`}>
                        {normalizeText(member.role, "Team Member")}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {member.assigned_count !== undefined && (
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/15 text-white" : "bg-slate-900 text-slate-500"
                          }`}>
                          {member.assigned_count}
                        </span>
                      )}
                      <span className={`h-2 w-2 rounded-full ${active ? "bg-white" : "bg-emerald-400"}`} />
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="rounded-md border border-slate-800 bg-slate-950 px-4 py-8 text-center text-sm text-slate-500">
                No team members found.
              </div>
            )}
          </div>
        </aside>

        <section className="min-w-0 space-y-4">
          <div className="flex gap-3 overflow-x-auto pb-1">
            {totals.map((item) => {
              const isAssignedUsers = item.label === "Assigned Users";
              const Card = isAssignedUsers ? "button" : "div";
              return (
              <Card
                key={item.label}
                {...(isAssignedUsers ? { type: "button" as const, onClick: openAssignedUsers } : {})}
                className={`min-w-[132px] shrink-0 rounded-md border bg-slate-900/50 px-4 py-4 text-left ${isAssignedUsers ? "cursor-pointer border-blue-500/40 transition hover:border-blue-400 hover:bg-blue-500/10" : "border-slate-800"}`}
              >
                <div className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500" title={item.label}>{item.label}</div>
                <div className={`mt-2 text-2xl font-semibold ${item.tone}`}>{toCount(item.value)}</div>
                {isAssignedUsers && <div className="mt-1 text-[10px] font-semibold text-blue-300">View users</div>}
              </Card>
              );
            })}
          </div>

          <div className="rounded-md border border-slate-800 bg-slate-900/50 p-4">
            <div className="grid gap-3 lg:grid-cols-[1.2fr_1fr_1fr_1.2fr_auto]">
              <label className="space-y-1">
                <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  <Search size={13} />
                  Search User
                </span>
                <input
                  type="search"
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      fetchActivity(selectedMember, 0);
                    }
                  }}
                  placeholder="User ID, name, phone"
                  className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-blue-500"
                />
              </label>
              <label className="space-y-1">
                <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  <CalendarDays size={13} />
                  From
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500"
                />
              </label>
              <label className="space-y-1">
                <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  <CalendarDays size={13} />
                  To
                </span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Activity Type</span>
                <select
                  value={eventType}
                  onChange={(event) => setEventType(event.target.value)}
                  className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500"
                >
                  {EVENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    fetchActivity(selectedMember, 0);
                  }}
                  className="inline-flex h-10 items-center justify-center rounded-md bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-500"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-800 bg-slate-900/50">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-white">{selectedMember || "Select a team member"}</div>
                <div className="text-xs text-slate-500">Time-wise activity for users assigned to this member</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  Showing {activity.length} items
                </span>
              </div>
            </div>

            <div className="divide-y divide-slate-800">
              {loading && activity.length === 0 ? (
                <div className="flex items-center justify-center gap-3 px-4 py-16 text-sm text-slate-400">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500" />
                  Loading activity...
                </div>
              ) : activity.length ? (
                activity.map((item, index) => {
                  const style = eventStyles[item.event_type] || eventStyles.published;
                  const Icon = style.icon;
                  const isFailedTemplate = item.event_type === "template_failed";
                  const templateDetail = item.event_type === "template_sent" || item.event_type === "template_failed"
                    ? parseTemplateDetail(item.detail, item.template_name, item.template_parameters)
                    : null;
                  return (
                    <div key={`${item.activity_key || item.event_type}-${item.event_time}-${item.user_id}-${item.candidate_id}-${index}`} className="grid gap-3 px-4 py-4 lg:grid-cols-[180px_170px_minmax(0,1fr)_160px]">
                      <div className="text-sm text-slate-400">{formatDateTime(item.event_time)}</div>
                      <div>
                        <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-semibold ${style.className}`}>
                          <Icon size={13} />
                          {style.label}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <Link
                            to={`/users/user/${item.user_id}`}
                            className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-1 font-semibold text-blue-100 hover:border-blue-300/50 hover:text-white"
                          >
                            {normalizeText(item.user_name, item.user_id)}
                          </Link>
                          <AssignedUserBadge />
                          {item.user_is_premium && <PremiumBadge />}
                          {item.candidate_id && (
                            <>
                              <span className="text-slate-600">with</span>
                              <Link to={`/users/user/${item.candidate_id}`} className="font-semibold text-slate-200 hover:text-blue-300">
                                {normalizeText(item.candidate_name, item.candidate_id)}
                              </Link>
                              {item.candidate_is_premium && <PremiumBadge />}
                            </>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                          <span>{normalizeText(item.user_id)}</span>
                          {item.candidate_id && <span>Candidate: {item.candidate_id}</span>}
                          {item.detail && !item.event_type.includes("message") && !item.event_type.startsWith("template_") && <span>Score: {item.detail}</span>}
                          {item.source && <span>Source: {item.source}</span>}
                        </div>
                        {templateDetail && (
                          <button
                            type="button"
                            onClick={() =>
                              setTemplateModal({
                                title: templateDetail.title,
                                body: templateDetail.body,
                                language: item.template_language,
                                parameters: item.template_parameters,
                                sentAt: item.event_time,
                                userName: normalizeText(item.user_name, item.user_id),
                              })
                            }
                            className={`mt-2 block w-full max-w-sm rounded-2xl rounded-tr-md px-4 py-3 text-left text-white shadow-sm transition focus:outline-none focus-visible:ring-2 ${isFailedTemplate
                                ? "border border-red-500/40 bg-red-950/70 shadow-red-950/30 hover:bg-red-900/80 focus-visible:ring-red-300/70"
                                : "bg-blue-600 shadow-blue-950/30 hover:bg-blue-500 focus-visible:ring-blue-300/70"
                              }`}
                            title="View full template message"
                          >
                            <div className="flex items-center gap-2">
                              {isFailedTemplate ? (
                                <AlertCircle size={14} className="shrink-0 text-red-100" />
                              ) : (
                                <MessageCircle size={14} className="shrink-0 text-blue-100" />
                              )}
                              <span className="min-w-0 truncate text-sm font-semibold">{templateDetail.title}</span>
                            </div>
                            <div className={isFailedTemplate ? "mt-1 text-[11px] text-red-100/75" : "mt-1 text-[11px] text-blue-100/75"}>
                              Tap to view full message
                            </div>
                            <div className={isFailedTemplate ? "mt-2 text-right text-[10px] font-medium text-red-100/80" : "mt-2 text-right text-[10px] font-medium text-blue-100/80"}>
                              {formatDateTime(item.event_time)}
                            </div>
                          </button>
                        )}
                        {item.detail && item.event_type.includes("message") && (
                          <div className="mt-2 rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-300">
                            {item.detail}
                          </div>
                        )}
                        {item.reason && (item.event_type.includes("dislike") || item.event_type === "template_failed") && (
                          <div className={`mt-2 rounded-md border px-3 py-2 text-sm ${isFailedTemplate
                              ? "border-red-500/25 bg-red-950/20 text-red-100"
                              : "border-slate-800 bg-slate-950 text-slate-300"
                            }`}>
                            {isFailedTemplate && (
                              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-red-300/80">
                                Failure reason
                              </div>
                            )}
                            <div className="whitespace-pre-wrap break-words">{item.reason}</div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-start justify-end">
                        {item.acknowledged ? (
                          <div className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-right text-xs text-emerald-200">
                            <div className="inline-flex items-center gap-1 font-semibold">
                              <CheckCircle2 size={13} />
                              Acknowledged
                            </div>
                            <div className="mt-1 text-emerald-300/70">
                              {normalizeText(item.acknowledged_by_username || item.acknowledged_by_admin_id, "Team member")}
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={!item.activity_key || acknowledgingKey === item.activity_key}
                            onClick={() => handleAcknowledge(item)}
                            className="inline-flex h-9 items-center justify-center rounded-md border border-blue-500/40 bg-blue-600/15 px-3 text-xs font-semibold text-blue-200 transition hover:border-blue-400 hover:bg-blue-600/25 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {acknowledgingKey === item.activity_key ? "Saving..." : "Acknowledge"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-16 text-center text-sm text-slate-500">
                  No activity found for this member and filter.
                </div>
              )}
              {hasMore && (
                <div ref={loadMoreRef} className="p-4 text-center text-sm text-slate-500">
                  {loading && (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500" />
                      Loading more...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      {showAssignedUsers && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowAssignedUsers(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-blue-400">Assigned Users</p>
                <h3 className="mt-1 text-lg font-bold text-white">{selectedMember}</h3>
                <p className="mt-1 text-xs text-slate-500">{assignedUsersTotal} users assigned</p>
              </div>
              <button type="button" onClick={() => setShowAssignedUsers(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {assignedUsersLoading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500" />
                  Loading assigned users...
                </div>
              ) : assignedUsers.length ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {assignedUsers.map((user) => {
                    const userId = user.id || (user as any).user_id;
                    const userStatus = user.is_hold ? "On Hold" : user.is_active ? "Active" : "Inactive";
                    return (
                      <a
                        key={userId}
                        href={`/users/user/${encodeURIComponent(userId)}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 transition hover:border-blue-500/50 hover:bg-blue-500/5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-white">{user.name || userId}</div>
                            <div className="mt-1 text-xs font-mono text-blue-300">{userId}</div>
                          </div>
                          <span className="rounded bg-slate-800 px-2 py-1 text-[10px] font-bold text-slate-300">{toCount(user.progress)}%</span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                          <span>{user.gender || "Gender --"}</span>
                          <span>{userStatus}</span>
                          {user.is_verified && <span className="text-emerald-400">Verified</span>}
                        </div>
                        {user.location && <div className="mt-2 truncate text-xs text-slate-400">{user.location}</div>}
                        <div className="mt-2 border-t border-slate-800 pt-2 text-[11px] text-slate-500">
                          Assigned: <span className="font-medium text-slate-300">{user.assigned_at ? formatDateTime(user.assigned_at) : "Date not available"}</span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className="py-16 text-center text-sm text-slate-500">No assigned users found.</div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-slate-800 px-5 py-3">
              <span className="text-xs text-slate-500">Page {assignedUsersPage} of {Math.max(1, Math.ceil(assignedUsersTotal / 20))}</span>
              <div className="flex gap-2">
                <button type="button" disabled={assignedUsersPage <= 1 || assignedUsersLoading} onClick={() => fetchAssignedUsers(assignedUsersPage - 1)} className="rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 disabled:opacity-40">Previous</button>
                <button type="button" disabled={assignedUsersPage * 20 >= assignedUsersTotal || assignedUsersLoading} onClick={() => fetchAssignedUsers(assignedUsersPage + 1)} className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40">Next</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {templateModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setTemplateModal(null)}
          role="presentation"
        >
          <div
            className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-activity-template-title"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-5 py-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">WhatsApp Template</p>
                <h3 id="team-activity-template-title" className="mt-1 truncate text-lg font-bold text-slate-100">
                  {templateModal.title}
                </h3>
                {templateModal.language && (
                  <p className="mt-1 text-xs text-slate-500">Language: {templateModal.language}</p>
                )}
                {templateModal.userName && (
                  <p className="mt-1 text-xs text-slate-500">Sent to {templateModal.userName}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setTemplateModal(null)}
                className="rounded-full px-2 py-1 text-sm font-semibold text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close"
              >
                x
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <div>
                <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Message</p>
                <div className="whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-slate-900/80 px-4 py-3 text-sm leading-relaxed text-slate-200">
                  {templateModal.body}
                </div>
              </div>
              {templateParameterEntries.length > 0 && (
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Parameters</p>
                  <div className="flex flex-wrap gap-2">
                    {templateParameterEntries.map(([key, value]) => {
                      const stringValue = formatParameterValue(value);
                      const isUrl = /^https?:\/\//i.test(stringValue);
                      return (
                        <span
                          key={key}
                          className="inline-flex max-w-full flex-col gap-0.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs text-blue-100"
                        >
                          <span className="font-semibold text-blue-300">{key}</span>
                          {isUrl ? (
                            <a
                              href={stringValue}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="break-all underline underline-offset-2 hover:opacity-80"
                            >
                              {stringValue}
                            </a>
                          ) : (
                            <span className="break-words text-slate-200">{stringValue}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {templateModal.sentAt && (
                <div className="text-[10px] text-slate-600">
                  Sent {formatDateTime(templateModal.sentAt)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
